/** @jsx jsx */
import { Box, Card, Flex, Heading, jsx } from 'theme-ui'
import React, { useState, Fragment, useContext, useEffect } from 'react'
import styled from '@emotion/styled'
import { graphql, navigate } from 'gatsby'
import Img from 'gatsby-image'
import ColorButton from '../../components/shared/Buttons/ColorButton'
import CustomStarRating from '../shared/StarRating/StarRating'

import Link from '../shared/Typography/Link/Link'
import Body from '../shared/Typography/Body/Body'
import { parseVariantsFromTags } from '../../utils/parseTags'
import { generateVariantLink } from './ColorInfoBlock'
import { getOverlayTag, NewTag } from '../blocks/ProductHero'
import { CountryContext } from '../../gatsby/wrap-wroot-element'
import { fetchCurrency, fetchReachLocalize } from '../../utils/reachLocalize'
import formatPrice from '../../utils/formatPrice'
import Button from '../shared/Buttons/Button'
import { ShopifyContext } from '../Layout'
import useReachCurrency from '../../hooks/useReachCurrency'

export const Product = styled.div``

export const ProductCardImage = styled(Img)`
  border-top-left-radius: 1px;
  border-top-right-radius: 1px;
  width: 100%;
`

export const GET_PRODUCT_CARD = graphql`
  fragment ShopifyProductDetails on ShopifyProduct {
    id
    title
    availableForSale
    description
    shopifyId
    handle
    tags
    onlineStoreUrl
    avgReviewScore
    details
    variants {
      id
      title
      sku
      price
      compareAtPrice
      details
      shopifyId
    }
    images {
      id
      localFile {
        id
        childImageSharp {
          fluid(maxWidth: 700, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
    }
  }
`

export const ProductPrice = ({
  price,
  compareAtPrice,
  color = 'text',
  avgReviewScore,
  reviewCount,
  productPricePreText,
  includesSavingsPercentage = false,
  currency,
  textColor,
  textStyles = {},
  ...props
}) => {
  const hasCompareAt =
    (typeof compareAtPrice === 'number' ||
      typeof compareAtPrice === 'string') &&
    compareAtPrice > 0

  return (
    <Flex
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
      {...props}
    >
      {productPricePreText && (
        <Body
          sx={{
            fontSize: '11px',
            lineHeight: '16px',
            marginRight: '5px',
            letterSpacing: '.5px',
            fontWeight: 300,
          }}
        >
          {productPricePreText}
        </Body>
      )}

      <Box>
        <Body
          sx={{
            fontSize: '14px',
            lineHeight: '14px',
            color: textColor || (hasCompareAt ? 'compareAtPrice' : 'text'),
            textTransform: 'uppercase',
            letterSpacing: '.5px',
            ...textStyles,
          }}
        >
          {price && formatPrice(price, undefined, currency || undefined)}
        </Body>
      </Box>
      {hasCompareAt && (
        <Box sx={{ pl: '5px' }}>
          <Body
            sx={{
              fontSize: '14px',
              lineHeight: '16px',
              color: textColor || (compareAtPrice ? 'gray' : 'text'),
              textTransform: 'uppercase',
              textDecoration: 'line-through',
              letterSpacing: '.5px',
              fontWeight: 300,
              opacity: 0.6,
              ...textStyles,
            }}
          >
            {compareAtPrice &&
              formatPrice(compareAtPrice, undefined, currency || undefined)}
          </Body>
        </Box>
      )}
      {includesSavingsPercentage && hasCompareAt && (
        <Box>
          <Body
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: 'secondary',
              letterSpacing: '1px',
              ml: '5px',
              fontWeight: compareAtPrice ? 'semibold' : 'normal',
              ...textStyles,
            }}
          >
            ({Math.round((1 - price / compareAtPrice) * 100)}% OFF)
          </Body>
        </Box>
      )}
      {avgReviewScore && (
        <CustomStarRating
          name="productCardRating"
          rating={avgReviewScore}
          starDimension="15px"
          reviewCount={reviewCount}
        />
      )}
    </Flex>
  )
}

export const getSubTitle = st => {
  return st === 'NA' ? '' : st
}
export const ProductSubTitle = ({ subTitle, color, ...props }) => {
  return (
    <Body
      variant="caption"
      sx={{
        fontWeight: color ? 'semiBold' : 300,
        color: color || 'text',
        textTransform: 'uppercase',
      }}
      {...props}
    >
      {subTitle}
    </Body>
  )
}

const ProductCard = ({
  shopifyId,
  hideSwatches,
  clickable = true,
  reverse = false,
  ...props
}) => {
  // const { setNotSupportedCountry } = useContext(ShopifyContext)
  const variants = parseVariantsFromTags(props.tags, true)

  const [activeColor, setActiveColor] = useState(variants['materials'][0])

  const subTitle = getSubTitle(props.details.subTitle)
  const subTitleColor = props.details.subTitleColor
  const overlayTag = getOverlayTag(props.tags)

  useEffect(() => {
    setActiveColor(variants['materials'][0])
  }, [variants])

  const {
    getRegionalPrice,
    getRegionalCompareAt,
    loaded,
    convertedCurrency,
    notUs,
    ConsumerPrices,
  } = useReachCurrency(props.variants)

  const regionalCompareAt = getRegionalCompareAt(0)
  const regionalPrice = getRegionalPrice(0)
  const imageIndex1 = reverse ? 0 : 1
  const imageIndex0 = reverse ? 1 : 0

  return (
    <Card {...props}>
      <Product featured={props.featured}>
        <Link
          to={clickable && `products/${props.handle}`}
          sx={{
            textDecoration: 'none',
            webkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
          }}
          linkProps={{
            sx: {
              lineHeight: 'inherit',
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {/* {overlayTag && <NewTag text={overlayTag} smallTag />} */}
            <ProductCardImage
              fluid={
                props.images[imageIndex1] && props.images[imageIndex1].localFile
                  ? props.images[imageIndex1].localFile.childImageSharp.fluid
                  : props.images[imageIndex0] &&
                    props.images[imageIndex0].localFile
              }
              backgroundColor={'#eeeeee'}
              objectFit="cover"
              sx={{ pointerEvents: ['none', 'none', 'auto'] }}
              style={{
                position:
                  props.images[imageIndex1] &&
                  props.images[imageIndex1].localFile
                    ? 'absolute'
                    : null,
              }}
            />
            {props.images[imageIndex0] && props.images[imageIndex0].localFile && (
              <ProductCardImage
                fluid={
                  props.images[imageIndex0].localFile.childImageSharp.fluid
                }
                backgroundColor={'#eeeeee'}
                objectFit="cover"
                sx={{
                  pointerEvents: ['none', 'none', 'auto'],
                  transition: 'opacity 0.25s ease',
                  opacity: '0',
                  zIndex: '1000',
                  '&:hover': {
                    opacity: '1',
                  },
                }}
              />
            )}
          </Box>

          {/* {subTitle && (
            <ProductSubTitle
              style={{ minHeight: !subTitle && '16px' }}
              subTitle={subTitle}
              color={subTitleColor}
            />
          )} */}

          <Flex
            sx={{
              flexWrap: 'wrap',
              justifyContent: ['space-between'],
              alignItems: 'center',
              mt: '10px',
              mx: props.carouselMode && '8px',
            }}
          >
            <Heading
              as="h6"
              sx={{
                marginTop: '3px',
                letterSpacing: '.2px',
                fontWeight: 'semiBold',
                fontSize: [1, 1, 2],
                width: ['100%'],
                marginBottom: ['0px', null, 'none'],
              }}
            >
              {props.title}
            </Heading>

            {loaded && (
              <ProductPrice
                price={
                  typeof regionalPrice === 'number'
                    ? regionalPrice
                    : props.variants[0].price
                }
                compareAtPrice={
                  typeof regionalCompareAt === 'number'
                    ? regionalCompareAt
                    : props.variants[0].compareAtPrice
                }
                productPricePreText={props.productPricePreText}
                color={subTitleColor}
                sx={{ py: '5px', justifyContent: 'flex-start' }}
                currency={convertedCurrency}
              />
            )}
          </Flex>
        </Link>
        {!hideSwatches && (
          <Flex
            sx={{ marginLeft: '-5px', textAlign: 'left', alignItems: 'center' }}
          >
            {variants.materials.map((color, index) => {
              return (
                <ColorButton
                  onClick={c => {
                    navigate(
                      `/products/${generateVariantLink(props.handle, color)}`
                    )
                  }}
                  isActive={activeColor === color}
                  key={index}
                  color={color}
                />
              )
            })}{' '}
          </Flex>
        )}
      </Product>
    </Card>
  )
}

export const FeaturedProduct = ({ k, ...props }) => {
  const { setNotSupportedCountry } = useContext(ShopifyContext)
  const [loaded, setLoaded] = useState(false)
  const [presentmentPrices, setPresentmentPrices] = useState(false)
  const geoState = useContext(CountryContext)

  useEffect(() => {
    if (geoState.country && geoState.country !== 'US') {
      fetchCurrency(
        props.variants,
        geoState.country,
        setPresentmentPrices,
        setLoaded,
        setNotSupportedCountry
      )
    }
    if (geoState.country === 'US') {
      setLoaded(true)
    }
  }, [geoState])

  const regionalCompareAt =
    presentmentPrices &&
    presentmentPrices.ConsumerPrices &&
    presentmentPrices.ConsumerPrices[
      0 + presentmentPrices.ConsumerPrices.length / 2
    ]

  const regionalPrice =
    presentmentPrices &&
    presentmentPrices.ConsumerPrices &&
    presentmentPrices.ConsumerPrices[0]
  return (
    <Flex
      sx={{
        flexDirection: k % 2 === 0 ? 'row' : 'row-reverse',
        alignItems: 'center',

        flexWrap: 'wrap-reverse',
        width: '100%',
      }}
      key={k}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          px: [10, 45],
          width: ['100%', '100%', '50%'],
          textAlign: ['left', null, null, k % 2 === 0 ? 'right' : 'left'],
          alignItems: [
            'flex-start',
            null,
            null,
            k % 2 === 0 ? 'flex-end' : 'flex-start',
          ],
        }}
      >
        {props.details.subTitle && (
          <ProductSubTitle
            sx={{ mb: '10px' }}
            subTitle={getSubTitle(props.details.subTitle)}
          />
        )}

        <Link to={`products/${props.handle}`}>
          <Heading
            as="h5"
            variant="bold"
            sx={{
              mt: [12, 12, 0],
              mb: [10, 10, 15],
              marginX: 'auto',

              ':hover': {
                opacity: '.7',
              },
            }}
          >
            {props.title}
          </Heading>
        </Link>

        <Body
          variant="body2"
          sx={{
            mb: [15, 15, 25],
            maxWidth: '450px',
            // marginX: 'auto',
          }}
        >
          {props.details.description || props.description}
        </Body>

        {loaded && (
          <ProductPrice
            textColor="black"
            style={{
              marginBottom: '20px',
              justifyContent: 'flex-start',
              color: 'white',
            }}
            price={
              typeof regionalPrice === 'number'
                ? regionalPrice
                : props.variants[0].price
            }
            compareAtPrice={
              typeof regionalCompareAt === 'number'
                ? regionalCompareAt
                : props.variants[0].compareAtPrice
            }
            productPricePreText={props.productPricePreText}
            currency={
              presentmentPrices &&
              typeof regionalPrice === 'number' &&
              presentmentPrices.Currency
            }
          />
        )}

        <Button
          styles={{
            minWidth: '200px',
            height: '48px',
          }}
          variant="black"
          to={`products/${props.handle}`}
          paragraphOptions={{
            style: {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          Shop
        </Button>
        {/* <AddToCart
              styles={{ minWidth: '200px', height: '48px' }}
              variant="default"
              shopifyId={i && props.variants[0].shopifyId}
              availableForSale={i && props.variants[0].availableForSale}
              productType={props.productType}
            >
              Add To Cart
            </AddToCart> */}
      </Flex>
      <Box
        sx={{
          position: 'relative',
          px: [0, 0],
          pt: [25, 25, 0],
          width: ['100%', '100%', '50%'],
        }}
      >
        <Link to={`products/${props.handle}`}>
          <Img fluid={props.images[0].localFile.childImageSharp.fluid} />
        </Link>
      </Box>
    </Flex>
  )
}

export default ProductCard
